import { Box, BoxProps, SvgIconProps, Tabs, TabsProps, styled, tabsClasses } from "@mui/material";
import { forwardRef } from "react";

import AppIcon, { AppIconProps } from "../Icons/AppIcon";

export type AppTabsProps = TabsProps & {
  isFullHeight?: boolean;
  containerSx?: BoxProps["sx"];
};

const TabsStyled = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "isFullHeight",
})<AppTabsProps>(({ theme, isFullHeight }) => ({
  minHeight: "unset",
  ...(isFullHeight && {
    height: "100%",
    [`& .${tabsClasses.scroller}`]: { height: "100%" },
    [`& .${tabsClasses.list}`]: { height: "100%" },
  }),
}));

/** Extends MUI Tabs. */
export default forwardRef<HTMLDivElement, AppTabsProps>(function AppTabs(
  {
    variant = "scrollable",
    scrollButtons = "auto",
    allowScrollButtonsMobile = true,
    slotProps,
    isFullHeight = false,
    sx,
    containerSx,
    children,
    ...otherProps
  }: AppTabsProps,
  ref,
) {
  return (
    <Box
      ref={ref}
      data-testid='AppTabs'
      sx={{ maxWidth: "100%", ...(isFullHeight && { height: "100%" }), ...containerSx }}
    >
      <TabsStyled
        data-testid='AppTabs-TabsStyled'
        sx={sx}
        variant={variant}
        scrollButtons={scrollButtons}
        allowScrollButtonsMobile={allowScrollButtonsMobile}
        isFullHeight={isFullHeight}
        slots={{
          startScrollButtonIcon: AppIcon,
          endScrollButtonIcon: AppIcon,
        }}
        slotProps={{
          startScrollButtonIcon: {
            of: "keyboardDoubleArrowLeft",
          } satisfies AppIconProps as SvgIconProps,
          endScrollButtonIcon: {
            of: "keyboardDoubleArrowRight",
          } satisfies AppIconProps as SvgIconProps,
          ...slotProps,
        }}
        {...otherProps}
      >
        {children}
      </TabsStyled>
    </Box>
  );
});
